import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import queryString from "query-string";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../../auth/AuthProvider";
import Page, {
  PageContent,
  PageProps,
  StyledPage,
} from "../../formatting/Page";
import { onDesktop } from "../../shared/layout/Breakpoints";
import { CreateContractContext } from "../create-contract/CreateContract";
import { FaqSection } from "../FaqSection";
import { DummySection } from "./DummySection";
import { ReviewSection, ReviewSectionHeader } from "./ReviewSection";
import { useSavedContractsContext } from "./SavedContracts";
import StaticHeader from "../../layout/header/StaticHeader";
import UpgradeSection from "./UpgradeSection";
import CreateContractCard, {
  CreateContractCardProps,
} from "./CreateContractCard";
import {
  ContractSource,
  ContractSourceName,
} from "@vehicle-contract/common/lib/model/contract/ContractSource";
import { ITheme, onMinWidthXtraLarge } from "@teamscheme/theme";
import CarryingImg from "./illustrations/woman_carrying_car_above_head_landingpage_284x227.svg";
import PayWithVippsBanner from "./PayWithVippsBanner";

const GreenStaticHeader = styled(StaticHeader)`
  background: ${({ theme }) => theme.nafColor.primary.dew};
`;

const LandingPageWrapper = styled.div``;

const LandingPageInfo = styled.div<{ green?: boolean }>`
  background: ${({ green, theme }) =>
    green ? theme.nafColor.primary.dew : theme.nafColor.signature.white};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.space32}
    ${({ theme }) => theme.spacing.space24} 0px;
  ${({ theme }) => onDesktop`
    padding: ${theme.spacing.space160} ${theme.spacing.space24} 0px;
  `}
`;

const AllInOneLandingPageInfo = styled(LandingPageInfo)`
  padding: 0;
  ${({ theme }) => onDesktop`
    padding: 0 ${theme.spacing.space24};
  `}
`;

function landingPageHeroSizes(theme: ITheme) {
  const viewportCarryingIllustration = {
    width: 284,
    height: 227,
  };
  const aspectRatioCarryingIllustration =
    viewportCarryingIllustration.height / viewportCarryingIllustration.width;
  const maxWidthCarryingIllustration = 320;

  return {
    paddingTopHeroDesktop: theme.spacing.space20,
    paddingTopHeroLargeDesktop: theme.spacing.space80,
    maxWidthCarryingIllustration: maxWidthCarryingIllustration + "px",

    maxHeightCarryingIllustration:
      maxWidthCarryingIllustration * aspectRatioCarryingIllustration + "px",
    heightH1: theme.fontStyle.header1["font-size"],
    paddingCreateReportCard: theme.spacing.space48,
    maxWidthCreateReportCard: 650 + "px",
  };
}

const Hero = styled.div`
  width: 100%;
  position: relative;
  padding: 0 ${({ theme }) => theme.spacing.space8};

  ${({ theme }) => onDesktop`
    padding: ${landingPageHeroSizes(theme).paddingTopHeroDesktop} ${
    theme.spacing.space24
  } 0;
  `}
  ${({ theme }) => onMinWidthXtraLarge`
    padding-top: ${landingPageHeroSizes(theme).paddingTopHeroLargeDesktop};
  `}
`;

const HeroContent = styled.div`
  margin: auto;
  max-width: ${({ theme }) => theme.customBreakpoints.maxHeroWidth}px;
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  align-items: center;
  z-index: 1;

  ${onMinWidthXtraLarge`
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
  `}
`;

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 0 ${({ theme }) => theme.spacing.space64};

  img {
    width: 100%;
    height: auto;
    max-width: ${({ theme }) =>
      landingPageHeroSizes(theme).maxWidthCarryingIllustration};
  }

  ${({ theme }) => onDesktop`
    padding: 0 ${theme.spacing.space8};
  `};
  ${onMinWidthXtraLarge`
    padding: 0;
  `};
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  background: ${({ theme }) => theme.nafColor.primary.dew};
  height: 50%;
  max-height: 50vh;
  width: 100%;
  margin: 0 -${({ theme }) => theme.spacing.space8};

  ${({ theme }) => {
    const {
      paddingTopHeroDesktop,
      maxHeightCarryingIllustration,
      heightH1,
      paddingCreateReportCard,
    } = landingPageHeroSizes(theme);
    return onDesktop`
      height: calc(
        ${paddingTopHeroDesktop} +
        ${maxHeightCarryingIllustration} +
        ${heightH1} +
        ${paddingCreateReportCard}
      );
      margin: 0 -${theme.spacing.space24};
    `;
  }}

  ${({ theme }) => {
    const { paddingTopHeroLargeDesktop, maxHeightCarryingIllustration } =
      landingPageHeroSizes(theme);
    return onMinWidthXtraLarge`
      height: calc(
        ${paddingTopHeroLargeDesktop} +
        ${maxHeightCarryingIllustration}
      );
    `;
  }}
`;

export function useForcedLoginForMembershipApp(
  source: ContractSource | ContractSourceName | undefined,
  currentUrl: string
) {
  const auth = useAuth();
  const { isAuthenticated, loginWithRedirect } = auth;

  const openLogin = useCallback(
    () => loginWithRedirect({ targetUrl: currentUrl }),
    [loginWithRedirect, currentUrl]
  );

  const isMatch = useMemo(() => {
    if (!source) return false;
    if (typeof source === "string") {
      return (
        source.toLowerCase() === ContractSourceName.MembershipApp.toLowerCase()
      );
    }

    return source === ContractSource.MembershipApp;
  }, [source]);

  useEffect(() => {
    if (isMatch && !isAuthenticated) {
      openLogin();
    }
  }, [isMatch, isAuthenticated, openLogin]);
}

const StyledCreateContract = styled(function (props: CreateContractCardProps) {
  return <CreateContractCard {...props} />;
})`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.space24};

  ${({ theme }) => onDesktop`
    max-width: ${landingPageHeroSizes(theme).maxWidthCreateReportCard};
    padding: ${landingPageHeroSizes(theme).paddingCreateReportCard};
  `};
`;

export const LandingPage = styled(
  ({ className, ...props }: Omit<PageProps, "children">) => {
    const createContractContext = useContext(CreateContractContext);
    if (!createContractContext) {
      throw new Error("No CreateContractContext found!");
    }
    const { create } = createContractContext;

    const savedContractsContext = useSavedContractsContext();
    const { savedContracts, hasSavedContracts, remove } = savedContractsContext;
    const [showForm, setShowForm] = useState(false);
    const [hasClickedShowForm, setHasClickedShowForm] = useState(false);

    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [bodyMinHeight, setBodyMinHeight] = useState<number | undefined>(
      undefined
    );

    useEffect(() => {
      if (!bodyMinHeight && bodyRef.current) {
        const timeout = setTimeout(() => {
          if (bodyRef.current) {
            setBodyMinHeight(bodyRef.current.getBoundingClientRect().height);
          }
        });

        return () => clearTimeout(timeout);
      }
    }, [bodyMinHeight]);

    const createCard = useRef<HTMLDivElement | null>(null);

    const scrollToCreateCard = useCallback(() => {
      if (createCard.current) {
        const rect = createCard.current.getBoundingClientRect();

        const isContained =
          rect.y >= 0 &&
          rect.x >= 0 &&
          rect.y + rect.height <= window.innerHeight &&
          rect.x + rect.width <= window.innerWidth;

        if (!isContained) {
          createCard.current.scrollIntoView();
        }
      }
    }, []);

    const location = useLocation<{ scrollToCreate?: boolean } | undefined>();
    const shouldScrollToCreate = location.state?.scrollToCreate || false;
    useEffect(() => {
      if (shouldScrollToCreate) {
        scrollToCreateCard();
      }
    }, [shouldScrollToCreate, scrollToCreateCard]);

    useEffect(() => {
      if (hasClickedShowForm) scrollToCreateCard();
    }, [showForm, hasClickedShowForm, scrollToCreateCard]);

    const lpnInput = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (showForm) {
        const timeout = setTimeout(() => {
          if (lpnInput.current) lpnInput.current.focus();
        });

        return () => clearTimeout(timeout);
      }
    }, [showForm]);

    const query = queryString.parse(location.search);
    const source = query.source as ContractSourceName | undefined;
    const currentUrl = `${location.pathname}${location.search}`;

    useForcedLoginForMembershipApp(source, currentUrl);

    return (
      <LandingPageWrapper className={className}>
        <GreenStaticHeader />
        <Page footer>
          <Hero>
            <HeroContent>
              <StyledCreateContract
                bodyRef={bodyRef}
                createCardRef={createCard}
                hasSavedContracts={hasSavedContracts}
                savedContracts={savedContracts}
                showForm={showForm}
                setShowForm={setShowForm}
                setHasClickedShowForm={setHasClickedShowForm}
                remove={remove}
                create={create}
                lpnInputRef={lpnInput}
              />
              <ImageWrapper>
                <img
                  src={CarryingImg}
                  alt="NAF Kjøpekontrakt"
                  width="284"
                  height="227"
                />
              </ImageWrapper>
            </HeroContent>
            <HeroBackground />
          </Hero>
          <LandingPageInfo>
            <PageContent>
              <PayWithVippsBanner />
            </PageContent>
          </LandingPageInfo>
          <AllInOneLandingPageInfo>
            <PageContent>
              <UpgradeSection />
            </PageContent>
          </AllInOneLandingPageInfo>
          <LandingPageInfo green>
            <PageContent>
              <ReviewSectionHeader />
              <ReviewSection />
            </PageContent>
          </LandingPageInfo>
          <LandingPageInfo>
            <PageContent>
              <DummySection
                onStartNew={() => {
                  setHasClickedShowForm(true);
                  setShowForm(true);
                }}
              />
            </PageContent>
          </LandingPageInfo>
          <LandingPageInfo>
            <PageContent>
              <FaqSection />
            </PageContent>
          </LandingPageInfo>
        </Page>
      </LandingPageWrapper>
    );
  }
)`
  ${StyledPage} {
    ${onDesktop`
      padding: 0;
    `}
  }

  ${PageContent} {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    max-width: ${({ theme }) => theme.customBreakpoints.maxHeroWidth}px;
  }
  ${LandingPageInfo}:last-child {
    margin-bottom: ${({ theme }) => theme.spacing.space160};
  }
`;

export default LandingPage;
